import { CATEGORICAL_COLORS, CHART_SERIES, seriesStrokeDash } from './palette';
import { ChartSeriesSwatch } from './ChartSeriesSwatch';
import { NmSegmentControl } from '../NmSegmentControl';
import { displayDigits } from '../../utils/displayDigits';
import React, { useMemo, useState, useCallback, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NmLoader } from '../NmLoadingOverlay';
import { useChartScrub } from '../../hooks/useChartScrub';
import { usePlotContainerWidth } from '../../hooks/usePlotContainerWidth';
import { formatApiDateCompact } from '../../utils/calendarPickerLocale';
import { PdcCompareRangeControls } from './PdcCompareRangeControls';
import type { PdcComparePreset } from '../../utils/pdcCompareRange';

export interface PdcPoint {
  key: string;
  interval: number;
  label: string;
  value: number;
  /** Workout that produced this peak (compare curve from API; current curve from parent workout). */
  workoutId?: number;
}

function parsePeakWorkoutId(raw: unknown): number | undefined {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export type PeakType = 'pw_peak' | 'hr_peak' | 'sd_peak' | 'cd_peak';
export type PdcValueScale = 'absolute' | 'perKg';

export interface PdcChartSeries {
  id: string;
  label: string;
  color: string;
  points: PdcPoint[];
  workoutId?: number;
  weightKg?: number | null;
}

/** Distinct line colors for batch compare (up to 10 workouts). */
export const BATCH_PDC_COLORS = CATEGORICAL_COLORS;

const PEAK_META: { type: PeakType; tabKey: string; titleKey: string; unit: string }[] = [
  { type: 'pw_peak', tabKey: 'pdcChart.tab.power', titleKey: 'pdcChart.peakTitle.power', unit: 'W' },
  { type: 'hr_peak', tabKey: 'pdcChart.tab.hr', titleKey: 'pdcChart.peakTitle.hr', unit: 'bpm' },
  { type: 'sd_peak', tabKey: 'pdcChart.tab.speed', titleKey: 'pdcChart.peakTitle.speed', unit: 'km/h' },
  { type: 'cd_peak', tabKey: 'pdcChart.tab.cadence', titleKey: 'pdcChart.peakTitle.cadence', unit: 'rpm' },
];

const CHART_BG   = '#DDE2EB';
const GRID       = 'rgba(163,177,198,.35)';
const TEXT       = '#1A1F2B';
const TEXT_MUTED = 'var(--chart-label, #9AA1B1)';
const COMPARE = CHART_SERIES.secondary;
const CURRENT = CHART_SERIES.primary;
const CURRENT_EDGE = 'rgba(255,255,255,.85)';
const SUMMARY_AXIS_TARGETS = [5, 60, 600, 3600, 5400, 10800] as const;

/** Normalize backend peak object to sorted curve points. */
export function normalizePeakCurve(
  raw: Record<string, unknown> | null | undefined,
  defaultWorkoutId?: number,
): PdcPoint[] {
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .map(([key, val]) => {
      const p = val as { value?: unknown; interval?: unknown; label?: unknown; workout_id?: unknown };
      return {
        key,
        interval: Number(p?.interval ?? 0),
        label: String(p?.label ?? key),
        value: Number(p?.value),
        workoutId: parsePeakWorkoutId(p?.workout_id) ?? defaultWorkoutId,
      };
    })
    .filter(p => isFinite(p.interval) && isFinite(p.value) && p.value > 0)
    .sort((a, b) => a.interval - b.interval);
}

function fmtDisplayDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function subtractYears(iso: string, years: number): string {
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
}

export function defaultCompareRange(workoutDay: string): { start: string; end: string } {
  const end = workoutDay?.slice(0, 10) || new Date().toISOString().slice(0, 10);
  return { start: subtractYears(end, 2), end };
}

function logX(interval: number, minI: number, maxI: number, ml: number, cw: number): number {
  const lo = Math.log10(Math.max(minI, 1));
  const hi = Math.log10(Math.max(maxI, minI + 1));
  const v  = Math.log10(Math.max(interval, 1));
  return ml + ((v - lo) / (hi - lo || 1)) * cw;
}

function valueToPlotY(value: number, yMin: number, yMax: number, mt: number, ch: number): number {
  const range = yMax - yMin || 1;
  return mt + ch - ((value - yMin) / range) * ch;
}

function buildAreaPath(
  points: PdcPoint[],
  minI: number,
  maxI: number,
  ml: number,
  mt: number,
  cw: number,
  ch: number,
  yMin: number,
  yMax: number,
): string {
  if (points.length < 2) return '';
  const baseY = mt + ch;
  const coords = points.map(p => {
    const x = logX(p.interval, minI, maxI, ml, cw);
    const y = valueToPlotY(p.value, yMin, yMax, mt, ch);
    return `${x},${y}`;
  });
  const firstX = logX(points[0].interval, minI, maxI, ml, cw);
  const lastX  = logX(points[points.length - 1].interval, minI, maxI, ml, cw);
  return `M ${firstX},${baseY} L ${coords.join(' L ')} L ${lastX},${baseY} Z`;
}

type PlotCoord = { x: number; y: number };

function pointsToPlotCoords(
  points: PdcPoint[],
  minI: number,
  maxI: number,
  ml: number,
  mt: number,
  cw: number,
  ch: number,
  yMin: number,
  yMax: number,
): PlotCoord[] {
  return points.map((p) => ({
    x: logX(p.interval, minI, maxI, ml, cw),
    y: valueToPlotY(p.value, yMin, yMax, mt, ch),
  }));
}

/** Tick step for W/kg — watt steps (20/50) collapse close athletes onto one band. */
function perKgYStep(span: number): number {
  if (span <= 1.5) return 0.25;
  if (span <= 3) return 0.5;
  if (span <= 6) return 1;
  if (span <= 16) return 2;
  return 2.5;
}

function buildYTicks(yMin: number, yMax: number, yStep: number): number[] {
  const ticks: number[] = [];
  const n = Math.round((yMax - yMin) / yStep);
  for (let i = 0; i <= n; i++) {
    ticks.push(Math.round((yMin + i * yStep) * 1000) / 1000);
  }
  return ticks;
}

/** Keep the numeric chart axis ASCII/LTR in every locale. */
export function formatPdcXAxisTick(interval: number): string {
  const seconds = Math.round(interval);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds < 60) return `${seconds}s`;
  if (seconds % 3600 === 0) return `${seconds / 3600}h`;
  if (seconds % 60 === 0) return `${seconds / 60}m`;
  return `${seconds}s`;
}

/** Compact Y-axis labels — avoid long decimals and keep tabular alignment. */
function formatPdcYTick(value: number, perKg: boolean): string {
  if (perKg) {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  }
  const rounded = Math.round(value);
  if (Math.abs(rounded) >= 1000) {
    const k = rounded / 1000;
    return Number.isInteger(k) ? `${k}k` : `${k.toFixed(1)}k`;
  }
  return String(rounded);
}

/**
 * Y-axis bounds — summary/batch use a tight range so curves fill the plot.
 * `perKg` uses fine steps and keeps yMin near the data floor (not 0→20).
 */
export function computePdcYAxis(
  allPoints: PdcPoint[],
  tight: boolean,
  perKg = false,
): { yMin: number; yMax: number; yTicks: number[] } {
  const maxVal = Math.max(...allPoints.map(p => p.value));
  const minVal = Math.min(...allPoints.map(p => p.value));

  if (perKg) {
    if (!tight) {
      const yCeil = Math.max(Math.ceil(maxVal * 2) / 2, 1);
      const yStep = perKgYStep(yCeil);
      const yMax = Math.ceil(yCeil / yStep) * yStep;
      return { yMin: 0, yMax, yTicks: buildYTicks(0, yMax, yStep) };
    }

    // Zoom into the W/kg band so 3.9 vs 4.3 separates instead of sitting on a 0–20 axis.
    const span = Math.max(maxVal - minVal, 0.4);
    const pad = Math.max(span * 0.08, 0.15);
    const rawMin = Math.max(0, minVal - pad);
    const rawMax = maxVal + pad;
    const rawSpan = Math.max(rawMax - rawMin, 0.5);
    const yStep = perKgYStep(rawSpan);
    let yMin = Math.floor(rawMin / yStep) * yStep;
    let yMax = Math.ceil(rawMax / yStep) * yStep;
    if (yMax <= yMin) yMax = yMin + yStep;
    // At least two steps so tick labels are readable — do not re-inflate toward a watts-scale empty axis.
    if ((yMax - yMin) / yStep < 2) {
      yMin = Math.max(0, Math.round((yMin - yStep) * 1000) / 1000);
      yMax = Math.round((yMax + yStep) * 1000) / 1000;
    }
    return { yMin, yMax, yTicks: buildYTicks(yMin, yMax, yStep) };
  }

  if (!tight) {
    const yCeil = Math.ceil(maxVal / 50) * 50 || 100;
    const yStep = yCeil <= 200 ? 50 : yCeil <= 500 ? 100 : 250;
    return { yMin: 0, yMax: yCeil, yTicks: buildYTicks(0, yCeil, yStep) };
  }

  const span = Math.max(maxVal - minVal, maxVal * 0.05, 1);
  const pad = span * 0.03;
  const rawMin = Math.max(0, minVal - pad);
  const rawMax = maxVal + pad;
  const rawSpan = rawMax - rawMin;
  const yStep = rawSpan <= 80 ? 20 : rawSpan <= 200 ? 50 : rawSpan <= 500 ? 100 : 250;
  const yMin = Math.floor(rawMin / yStep) * yStep;
  const yMax = Math.ceil(rawMax / yStep) * yStep;
  return { yMin, yMax, yTicks: buildYTicks(yMin, yMax, yStep) };
}

/** Smooth cubic-bezier spline through plot coordinates (modern chart line). */
function buildSmoothLinePath(coords: PlotCoord[]): string {
  if (!coords.length) return '';
  if (coords.length === 1) return `M ${coords[0].x},${coords[0].y}`;

  let d = `M ${coords[0].x},${coords[0].y}`;
  for (let i = 0; i < coords.length - 1; i++) {
    const p0 = coords[Math.max(i - 1, 0)];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[Math.min(i + 2, coords.length - 1)];
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
}

function buildSmoothAreaPath(coords: PlotCoord[], baseY: number): string {
  if (coords.length < 2) return '';
  const line = buildSmoothLinePath(coords);
  const last = coords[coords.length - 1];
  const first = coords[0];
  return `${line} L ${last.x},${baseY} L ${first.x},${baseY} Z`;
}

function pickAxisLabels(
  points: PdcPoint[],
  opts?: {
    targets?: readonly number[];
    minLog10Gap?: number;
    minGapPx?: number;
    minI?: number;
    maxI?: number;
    ml?: number;
    cw?: number;
  },
): PdcPoint[] {
  if (!points.length) return [];
  const targets = opts?.targets ?? [5, 60, 600, 3600, 10800];
  const minLog10Gap = opts?.minLog10Gap ?? 0.45;
  const picked: PdcPoint[] = [];
  for (const t of targets) {
    let best = points[0];
    let bestDist = Math.abs(Math.log10(points[0].interval) - Math.log10(t));
    for (const p of points) {
      const dist = Math.abs(Math.log10(p.interval) - Math.log10(t));
      if (dist < bestDist) { best = p; bestDist = dist; }
    }
    if (picked.some(x => x.key === best.key)) continue;
    const tooClose = picked.some(
      p => Math.abs(Math.log10(p.interval) - Math.log10(best.interval)) < minLog10Gap,
    );
    if (!tooClose) picked.push(best);
  }
  const sorted = picked.sort((a, b) => a.interval - b.interval);
  if (
    opts?.minGapPx != null &&
    opts.minI != null &&
    opts.maxI != null &&
    opts.ml != null &&
    opts.cw != null
  ) {
    const out: PdcPoint[] = [];
    let lastX = -Infinity;
    for (const p of sorted) {
      const x = logX(p.interval, opts.minI, opts.maxI, opts.ml, opts.cw);
      if (out.length === 0 || x - lastX >= opts.minGapPx) {
        out.push(p);
        lastX = x;
      }
    }
    return out;
  }
  return sorted;
}

interface PdcHoverRow {
  interval: number;
  label: string;
  key: string;
  current: number | null;
  compare: number | null;
  currentWorkoutId?: number;
  compareWorkoutId?: number;
}

/** Workout with the highest peak at this duration (today vs historical best). */
export function resolveBestWorkoutIdAtRow(
  row: PdcHoverRow,
  fallbackCurrentWorkoutId?: number,
): number | null {
  const curId = row.currentWorkoutId ?? fallbackCurrentWorkoutId;
  const cmpId = row.compareWorkoutId;
  if (row.current != null && row.compare != null && curId && cmpId) {
    return row.current >= row.compare ? curId : cmpId;
  }
  return cmpId ?? curId ?? null;
}

/** Merge both curves by interval for hover lookup. */
interface PdcMultiHoverRow {
  interval: number;
  label: string;
  key: string;
  entries: Array<{
    seriesId: string;
    label: string;
    color: string;
    value: number;
    workoutId?: number;
  }>;
}

function scaleSeriesPoints(
  points: PdcPoint[],
  peakType: PeakType,
  scale: PdcValueScale,
  weightKg: number | null | undefined,
): PdcPoint[] {
  if (scale !== 'perKg' || peakType !== 'pw_peak') return points;
  if (weightKg == null || weightKg <= 0) return [];
  return points.map(p => ({ ...p, value: p.value / weightKg }));
}

/** Scales series points for export (same rules as on-screen PDC). */
export function scalePdcSeriesForExport(
  points: PdcPoint[],
  peakType: PeakType,
  scale: PdcValueScale,
  weightKg: number | null | undefined,
): PdcPoint[] {
  return scaleSeriesPoints(points, peakType, scale, weightKg);
}

function mergeMultiHoverRows(
  series: Array<{ id: string; label: string; color: string; points: PdcPoint[]; workoutId?: number }>,
): PdcMultiHoverRow[] {
  const map = new Map<number, PdcMultiHoverRow>();
  for (const s of series) {
    for (const p of s.points) {
      let row = map.get(p.interval);
      if (!row) {
        row = { interval: p.interval, label: p.label, key: p.key, entries: [] };
        map.set(p.interval, row);
      }
      row.entries.push({
        seriesId: s.id,
        label: s.label,
        color: s.color,
        value: p.value,
        workoutId: p.workoutId ?? s.workoutId,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.interval - b.interval);
}

function mergeHoverRows(current: PdcPoint[], compare: PdcPoint[]): PdcHoverRow[] {
  const map = new Map<number, PdcHoverRow>();
  for (const p of current) {
    map.set(p.interval, {
      interval: p.interval,
      label: p.label,
      key: p.key,
      current: p.value,
      compare: map.get(p.interval)?.compare ?? null,
      currentWorkoutId: p.workoutId,
      compareWorkoutId: map.get(p.interval)?.compareWorkoutId,
    });
  }
  for (const p of compare) {
    const row = map.get(p.interval);
    if (row) {
      row.compare = p.value;
      row.compareWorkoutId = p.workoutId;
      if (!row.label) row.label = p.label;
    } else {
      map.set(p.interval, {
        interval: p.interval,
        label: p.label,
        key: p.key,
        current: null,
        compare: p.value,
        compareWorkoutId: p.workoutId,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.interval - b.interval);
}

/** % delta at one duration vs historical best. */
function intervalDeltaPct(current: number, compare: number): number | null {
  if (!(compare > 0)) return null;
  return ((current - compare) / compare) * 100;
}

/** Average % delta across all matched intervals (overall curve comparison). */
function computeAverageCompareDeltaPct(current: PdcPoint[], compare: PdcPoint[]): number | null {
  if (!current.length || !compare.length) return null;
  const compareByInterval = new Map(compare.map((p) => [p.interval, p.value]));
  let sum = 0;
  let count = 0;
  for (const p of current) {
    const baseline = compareByInterval.get(p.interval);
    if (baseline != null && baseline > 0) {
      sum += ((p.value - baseline) / baseline) * 100;
      count += 1;
    }
  }
  if (count === 0) return null;
  return sum / count;
}

function formatDeltaPct(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function fmtPdcValue(value: number, unit: string): string {
  if (unit === 'km/h') return displayDigits(value.toFixed(1));
  if (unit === 'W/kg') return displayDigits(value.toFixed(2));
  return displayDigits(Math.round(value));
}

function intervalAtSvgX(svgX: number, minI: number, maxI: number, ml: number, cw: number): number {
  const lo = Math.log10(Math.max(minI, 1));
  const hi = Math.log10(Math.max(maxI, minI + 1));
  const t = Math.max(0, Math.min(1, (svgX - ml) / cw));
  return Math.pow(10, lo + t * (hi - lo));
}

function nearestHoverIndex<T extends { interval: number }>(
  svgX: number,
  rows: T[],
  minI: number,
  maxI: number,
  ml: number,
  cw: number,
): number {
  if (!rows.length) return -1;
  const target = intervalAtSvgX(svgX, minI, maxI, ml, cw);
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < rows.length; i++) {
    const dist = Math.abs(Math.log10(Math.max(rows[i].interval, 1)) - Math.log10(Math.max(target, 1)));
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best;
}

/** Map screen coordinates → SVG viewBox space (handles preserveAspectRatio letterboxing). */
function clientToSvgX(svg: SVGSVGElement, clientX: number): number {
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = 0;
  const ctm = svg.getScreenCTM();
  if (!ctm) return 0;
  return pt.matrixTransform(ctm.inverse()).x;
}

interface PdcCurveChartProps {
  cadenceUnit?: string;
  currentCurve: PdcPoint[];
  compareCurve: PdcPoint[];
  compareStart: string;
  compareEnd: string;
  currentLabel: string;
  peakType: PeakType;
  onPeakTypeChange: (type: PeakType) => void;
  onCompareStartChange: (v: string) => void;
  onCompareEndChange: (v: string) => void;
  /** Summary variant — preset chips instead of inline date inputs. */
  rangeEndDate?: string;
  onComparePresetApply?: (preset: PdcComparePreset, custom?: { start: string; end: string }) => void;
  loading?: boolean;
  /** SVG plot height in px (default 280). */
  chartHeight?: number;
  /** Fixed rendered plot height for aligned dashboard charts, independent of card width. */
  plotHeight?: number;
  /** `default` — analysis overlay. `summary` — compact neumorphic card. `batch` — multi-workout compare. */
  variant?: 'default' | 'summary' | 'batch';
  /** Taller plot chrome when opened in landscape fullscreen. */
  layoutMode?: 'inline' | 'fullscreen';
  /** Opens landscape fullscreen (summary card — button on plot). */
  onExpand?: () => void;
  /** Batch/summary — export plot as PNG (button beside expand). */
  onDownloadPng?: () => void;
  downloadingPng?: boolean;
  /** Current workout id — used when today's curve beats historical best at a duration. */
  currentWorkoutId?: number;
  /** Tap a chart point to open the best workout at that duration. */
  onBestWorkoutClick?: (workoutId: number) => void;
  /** In a workout detail, chart taps navigate only to a different, historical workout. */
  compareOnlyWorkoutClick?: boolean;
  /** Override primary series legend (e.g. workout A title in comparison). */
  currentLegendLabel?: string;
  /** Override compare series legend (e.g. workout B title in comparison). */
  compareLegendLabel?: string;
  /** Hide historical compare date controls (workout-vs-workout comparison). */
  hideCompareControls?: boolean;
  /** Hide the series legend when the card presents one selected-period curve. */
  hideLegend?: boolean;
  /** Multi-series mode (batch compare) — overrides current/compare curves. */
  multiSeries?: PdcChartSeries[];
  hiddenSeriesIds?: ReadonlySet<string>;
  onToggleSeries?: (seriesId: string) => void;
  valueScale?: PdcValueScale;
  onValueScaleChange?: (scale: PdcValueScale) => void;
  /** Batch compare — title in toolbar row (expand moves out of plot). */
  batchHeaderTitle?: string;
}

export function PdcCurveChart({
  cadenceUnit,
  currentCurve,
  compareCurve,
  compareStart,
  compareEnd,
  currentLabel,
  peakType,
  onPeakTypeChange,
  onCompareStartChange,
  onCompareEndChange,
  rangeEndDate,
  onComparePresetApply,
  loading,
  chartHeight = 360,
  plotHeight,
  variant = 'default',
  layoutMode = 'inline',
  onExpand,
  onDownloadPng,
  downloadingPng = false,
  currentWorkoutId,
  onBestWorkoutClick,
  compareOnlyWorkoutClick = false,
  currentLegendLabel,
  compareLegendLabel,
  hideCompareControls = false,
  hideLegend = false,
  multiSeries,
  hiddenSeriesIds,
  onToggleSeries,
  valueScale = 'absolute',
  onValueScaleChange,
  batchHeaderTitle,
}: PdcCurveChartProps) {
  const { t, i18n } = useTranslation();
  const isSummary = variant === 'summary';
  const isBatch = variant === 'batch';
  const isFsLayout = layoutMode === 'fullscreen';
  const isMulti = (multiSeries?.length ?? 0) > 0;

  const peakOptions = useMemo(
    () => PEAK_META.map(o => ({
      type: o.type,
      unit: o.type === 'cd_peak' ? cadenceUnit === 'spm' ? t('runningMetrics.cadenceUnit') : cadenceUnit ?? o.unit : o.unit,
      label: o.type === 'cd_peak' && cadenceUnit === 'spm' ? t('runningMetrics.cadence') : t(o.tabKey),
      title: t(o.titleKey),
    })),
    [t, cadenceUnit],
  );

  const axisLabelText = useCallback(
    (p: { key?: string; interval?: number; label?: string }) =>
      p.interval != null && Number.isFinite(p.interval)
        ? formatPdcXAxisTick(p.interval)
        : String(p.label ?? p.key ?? ''),
    [],
  );

  const meta = peakOptions.find(o => o.type === peakType) ?? peakOptions[0];
  const displayUnit = isMulti && valueScale === 'perKg' && peakType === 'pw_peak' ? 'W/kg' : meta.unit;
  const svgRef = useRef<SVGSVGElement>(null);
  const plotWrapRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);
  const wasScrubRef = useRef(false);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [tooltipLeftPx, setTooltipLeftPx] = useState(0);
  const [bestWorkoutSelection, setBestWorkoutSelection] = useState<{
    workoutId: number;
    row: PdcHoverRow;
  } | null>(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsNarrowViewport(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const W = 640;
  const isModern = isSummary || isFsLayout || isBatch;
  const useResponsivePlot = isModern && !isFsLayout;
  const plotMeasureKey = `${loading}:${isMulti ? multiSeries?.length ?? 0 : currentCurve.length}:${compareCurve.length}`;
  const plotContainerWidth = usePlotContainerWidth(plotWrapRef, useResponsivePlot, plotMeasureKey);
  const fallbackPlotWidth = typeof window !== 'undefined'
    ? Math.max(260, Math.min(Math.round(window.innerWidth - 32), W))
    : W;
  const effectivePlotWidth = plotContainerWidth > 0 ? plotContainerWidth : fallbackPlotWidth;
  const batchNarrow = isBatch && isNarrowViewport && !isFsLayout;
  /**
   * SVG text scales with viewBox. Prefer modest compensation so glyphs stay crisp
   * (~10–11px on phone) instead of huge SVG fontSizes that blur when downscaled.
   */
  const plotScale = effectivePlotWidth / W;
  const targetAxisScreenPx = isFsLayout ? 12 : isSummary || batchNarrow ? 10.5 : 9;
  const axisFs = useResponsivePlot
    ? Math.max(isSummary ? 12 : 10, Math.round(targetAxisScreenPx / Math.max(plotScale, 0.28)))
    : (isFsLayout ? 12 : isSummary ? 10 : batchNarrow ? 8 : 7);
  const ML = Math.max(
    batchNarrow ? 34 : isModern ? 40 : 48,
    Math.ceil(axisFs * (isSummary ? 3.2 : 3.0)),
  );
  const MR = batchNarrow ? 4 : isModern ? 6 : 16;
  const MT = isModern ? Math.max(4, Math.round(axisFs * 0.2)) : 28;
  const MB = Math.max(
    batchNarrow ? 12 : isModern ? 14 : 32,
    Math.ceil(axisFs + (isModern ? 10 : 12)),
  );
  /** Taller viewBox on narrow summary so the curve isn't a 140px strip with micro labels. */
  const summaryTargetCssH = plotHeight ?? (effectivePlotWidth < 520 ? 192 : Math.round(effectivePlotWidth * (chartHeight / W)));
  const H = useResponsivePlot && isSummary
    ? Math.max(chartHeight, Math.round(W * (summaryTargetCssH / Math.max(effectivePlotWidth, 1))))
    : chartHeight;
  const responsivePlotHeight = plotHeight ?? (useResponsivePlot
    ? Math.max(isSummary ? 168 : 140, Math.round(effectivePlotWidth * (H / W)))
    : H);
  const lineW = isFsLayout ? 3.4 : isModern ? 3 : 2;
  const markerR = isFsLayout ? 5.5 : isModern ? 4.5 : 3.5;
  const axisLabelY = H - Math.max(isModern ? 5 : 8, Math.round(axisFs * 0.7));
  const unitLabelX = Math.max(11, Math.round(ML * 0.28));
  const gradId = useId().replace(/:/g, '');
  const CW = W - ML - MR;
  const CH = H - MT - MB;

  const visibleMultiSeries = useMemo(() => {
    if (!isMulti || !multiSeries) return [];
    return multiSeries
      .filter(s => !hiddenSeriesIds?.has(s.id))
      .map(s => ({
        id: s.id,
        label: s.label,
        color: s.color,
        workoutId: s.workoutId,
        points: scaleSeriesPoints(s.points, peakType, valueScale, s.weightKg),
      }))
      .filter(s => s.points.length > 0);
  }, [hiddenSeriesIds, isMulti, multiSeries, peakType, valueScale]);

  const allPoints = useMemo(
    () => (isMulti ? visibleMultiSeries.flatMap(s => s.points) : [...currentCurve, ...compareCurve]),
    [compareCurve, currentCurve, isMulti, visibleMultiSeries],
  );

  const multiSeriesPlot = useMemo(() => {
    if (!isMulti || !allPoints.length) {
      return { seriesLines: [] as Array<{ id: string; color: string; dash?: string; linePath: string; coords: PlotCoord[] }> };
    }
    const intervals = allPoints.map(p => p.interval);
    const minInterval = Math.min(...intervals);
    const maxInterval = Math.max(...intervals);
    const { yMin: axisMin, yMax: axisMax } = computePdcYAxis(allPoints, isModern, valueScale === 'perKg');
    const seriesLines = visibleMultiSeries.map(s => {
      const coords = pointsToPlotCoords(s.points, minInterval, maxInterval, ML, MT, CW, CH, axisMin, axisMax);
      return { id: s.id, color: s.color, dash: seriesStrokeDash(multiSeries?.findIndex(item => item.id === s.id) ?? 0), coords, linePath: buildSmoothLinePath(coords) };
    });
    return { seriesLines };
  }, [CH, CW, ML, MT, allPoints, isModern, isMulti, valueScale, visibleMultiSeries]);

  const { minI, maxI, yMin, yMax, comparePath, currentPath, axisLabels, yTicks, compareLinePath, currentLinePath, currentAreaPath, compareAreaPath, currentCoords, compareCoords } = useMemo(() => {
    if (!allPoints.length) {
      return {
        minI: 0,
        maxI: 0,
        yMin: 0,
        yMax: 0,
        comparePath: '',
        currentPath: '',
        compareLinePath: '',
        currentLinePath: '',
        currentAreaPath: '',
        compareAreaPath: '',
        currentCoords: [] as PlotCoord[],
        compareCoords: [] as PlotCoord[],
        axisLabels: [] as PdcPoint[],
        yTicks: [] as number[],
      };
    }
    const intervals = allPoints.map(p => p.interval);
    const minInterval = Math.min(...intervals);
    const maxInterval = Math.max(...intervals);
    const { yMin: axisMin, yMax: axisMax, yTicks: ticks } = computePdcYAxis(
      allPoints,
      isModern,
      valueScale === 'perKg',
    );

    const compareCoords = pointsToPlotCoords(compareCurve, minInterval, maxInterval, ML, MT, CW, CH, axisMin, axisMax);
    const currentCoords = pointsToPlotCoords(currentCurve, minInterval, maxInterval, ML, MT, CW, CH, axisMin, axisMax);
    const baseY = MT + CH;

    return {
      minI: minInterval,
      maxI: maxInterval,
      yMin: axisMin,
      yMax: axisMax,
      comparePath: buildAreaPath(compareCurve, minInterval, maxInterval, ML, MT, CW, CH, axisMin, axisMax),
      currentPath: buildAreaPath(currentCurve, minInterval, maxInterval, ML, MT, CW, CH, axisMin, axisMax),
      compareLinePath: buildSmoothLinePath(compareCoords),
      currentLinePath: buildSmoothLinePath(currentCoords),
      compareAreaPath: buildSmoothAreaPath(compareCoords, baseY),
      currentAreaPath: buildSmoothAreaPath(currentCoords, baseY),
      currentCoords,
      compareCoords,
      axisLabels: pickAxisLabels(allPoints, {
        targets: isSummary ? SUMMARY_AXIS_TARGETS : undefined,
        minLog10Gap: isSummary ? 0.12 : isModern ? 0.48 : 0.35,
        minGapPx: isSummary
          ? Math.max(28, axisFs * 1.4)
          : isFsLayout
            ? 72
            : isModern
              ? Math.max(64, axisFs * 3.5)
              : 40,
        minI: minInterval,
        maxI: maxInterval,
        ml: ML,
        cw: CW,
      }),
      yTicks: ticks,
    };
  }, [allPoints, axisFs, compareCurve, currentCurve, CW, CH, ML, MT, isModern, isFsLayout, isSummary, valueScale]);

  const hasData = allPoints.length > 0;

  const dualHoverRows = useMemo(
    () => mergeHoverRows(currentCurve, compareCurve),
    [currentCurve, compareCurve],
  );

  const multiHoverRows = useMemo(
    () => mergeMultiHoverRows(visibleMultiSeries),
    [visibleMultiSeries],
  );

  const activeHoverRows = isMulti ? multiHoverRows : dualHoverRows;

  const hoverRow = hoverIdx != null && hoverIdx >= 0
    ? activeHoverRows[hoverIdx] ?? null
    : null;

  const handleScrub = useCallback((clientX: number) => {
    const svg = svgRef.current;
    const wrap = wrapRef.current;
    if (!svg || !(isMulti ? multiHoverRows.length : dualHoverRows.length) || !hasData) return;

    const svgX = clientToSvgX(svg, clientX);
    if (svgX < ML || svgX > W - MR) {
      setHoverIdx(null);
      return;
    }

    const idx = isMulti
      ? nearestHoverIndex(svgX, multiHoverRows, minI, maxI, ML, CW)
      : nearestHoverIndex(svgX, dualHoverRows, minI, maxI, ML, CW);
    setHoverIdx(idx);

    if (wrap) {
      const rect = wrap.getBoundingClientRect();
      setTooltipLeftPx(clientX - rect.left);
    }
  }, [dualHoverRows, isMulti, minI, maxI, ML, CW, multiHoverRows, W, hasData]);

  const scrub = useChartScrub(
    hasData && (isMulti ? multiHoverRows.length : dualHoverRows.length) > 0,
    handleScrub,
    () => setHoverIdx(null),
  );

  const hoverRowAtClientX = useCallback((clientX: number): PdcHoverRow | null => {
    if (isMulti) return null;
    const svg = svgRef.current;
    if (!svg || !dualHoverRows.length || !hasData) return null;
    const svgX = clientToSvgX(svg, clientX);
    if (svgX < ML || svgX > W - MR) return null;
    const idx = nearestHoverIndex(svgX, dualHoverRows, minI, maxI, ML, CW);
    return idx >= 0 ? dualHoverRows[idx] ?? null : null;
  }, [dualHoverRows, hasData, isMulti, minI, maxI, ML, CW, W]);

  const selectBestWorkoutAt = useCallback((clientX: number) => {
    if (!onBestWorkoutClick) return;
    const row = hoverRowAtClientX(clientX);
    if (!row) return;
    const id = compareOnlyWorkoutClick ? row.compareWorkoutId : resolveBestWorkoutIdAtRow(row, currentWorkoutId);
    if (id) setBestWorkoutSelection({ workoutId: id, row });
  }, [onBestWorkoutClick, hoverRowAtClientX, currentWorkoutId, compareOnlyWorkoutClick]);

  const plotPointerHandlers = useMemo(() => {
    const TAP_MAX_PX = 12;
    return {
      onPointerDown: (e: React.PointerEvent) => {
        tapStartRef.current = { x: e.clientX, y: e.clientY };
        wasScrubRef.current = false;
        scrub.onPointerDown(e);
      },
      onPointerMove: (e: React.PointerEvent) => {
        if (tapStartRef.current) {
          const dx = e.clientX - tapStartRef.current.x;
          const dy = e.clientY - tapStartRef.current.y;
          if (Math.hypot(dx, dy) > TAP_MAX_PX) wasScrubRef.current = true;
        }
        scrub.onPointerMove(e);
      },
      onPointerUp: (e: React.PointerEvent) => {
        const start = tapStartRef.current;
        const isTap = !!start
          && !wasScrubRef.current
          && Math.hypot(e.clientX - start.x, e.clientY - start.y) <= TAP_MAX_PX;
        scrub.onPointerUp(e);
        if (isTap) selectBestWorkoutAt(e.clientX);
        tapStartRef.current = null;
      },
      onPointerCancel: scrub.onPointerCancel,
      onPointerLeave: scrub.onPointerLeave,
    };
  }, [scrub, selectBestWorkoutAt]);

  useEffect(() => {
    setHoverIdx(null);
    setBestWorkoutSelection(null);
  }, [peakType, currentCurve, compareCurve, multiSeries, valueScale, hiddenSeriesIds]);

  useEffect(() => {
    if (!bestWorkoutSelection) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setBestWorkoutSelection(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [bestWorkoutSelection]);

  const fmtLegendDate = useCallback(
    (iso: string) => (isSummary ? formatApiDateCompact(iso, i18n.language) : fmtDisplayDate(iso)),
    [i18n.language, isSummary],
  );

  const currentSeriesLabel = currentLegendLabel ?? (isSummary ? t('pdcChart.today') : currentLabel);

  const compareDeltaPct = useMemo(
    () => computeAverageCompareDeltaPct(currentCurve, compareCurve),
    [currentCurve, compareCurve],
  );

  if (!isMulti && !currentCurve.length && !compareCurve.length && !loading) return null;
  if (isMulti && !allPoints.length && !loading) return null;

  const hoverX = hoverRow ? logX(hoverRow.interval, minI, maxI, ML, CW) : null;
  const hoverYCurrent = !isMulti && hoverRow && 'current' in hoverRow && hoverRow.current != null
    ? valueToPlotY(hoverRow.current, yMin, yMax, MT, CH)
    : null;
  const hoverYCompare = !isMulti && hoverRow && 'compare' in hoverRow && hoverRow.compare != null
    ? valueToPlotY(hoverRow.compare, yMin, yMax, MT, CH)
    : null;

  const compareLegend =
    compareLegendLabel ?? `${fmtLegendDate(compareStart)} – ${fmtLegendDate(compareEnd)}`;

  const compareDates = (
    <>
      <label className="pdc-chart__date-field">
        <input
          type="date"
          className="pdc-chart__date"
          value={compareStart}
          onChange={e => onCompareStartChange(e.target.value)}
          aria-label={t('pdcChart.from')}
        />
      </label>
      <span className="pdc-chart__date-sep" aria-hidden>–</span>
      <label className="pdc-chart__date-field">
        <input
          type="date"
          className="pdc-chart__date"
          value={compareEnd}
          onChange={e => onCompareEndChange(e.target.value)}
          aria-label={t('pdcChart.to')}
        />
      </label>
    </>
  );

  const expandInToolbar = isBatch && !!batchHeaderTitle && !!onExpand && !isFsLayout;

  const downloadButton =
    onDownloadPng && (isSummary || isBatch) && !isFsLayout ? (
      <button
        type="button"
        className={`pdc-chart__download-btn pdc-chart__icon-btn${expandInToolbar ? ' pdc-chart__batch-download' : ''}`}
        onClick={onDownloadPng}
        disabled={downloadingPng}
        aria-label={t('pdcChart.downloadPng')}
        title={t('pdcChart.downloadPng')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
        </svg>
      </button>
    ) : null;

  const expandButton =
    onExpand && (isSummary || isBatch) && !isFsLayout ? (
      <button
        type="button"
        className={`pdc-chart__expand-btn pdc-chart__icon-btn${expandInToolbar ? ' pdc-chart__batch-expand' : ''}`}
        onClick={onExpand}
        aria-label={t('pdcChart.expand')}
        title={t('pdcChart.expand')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden>
          <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />
        </svg>
      </button>
    ) : null;

  const compareRangeControl =
    isSummary && !hideCompareControls && rangeEndDate && onComparePresetApply ? (
      <PdcCompareRangeControls
        compareStart={compareStart}
        compareEnd={compareEnd}
        rangeEndDate={rangeEndDate}
        loading={loading}
        onApply={onComparePresetApply}
      />
    ) : null;

  const plotActionButtons =
    !expandInToolbar && (downloadButton || compareRangeControl || expandButton) ? (
      <div className="pdc-chart__plot-actions">
        {downloadButton}
        {compareRangeControl}
        {expandButton}
      </div>
    ) : null;

  const rootClass = [
    'pdc-chart',
    isSummary ? 'pdc-chart--summary' : isBatch ? 'pdc-chart--batch' : 'pdc-chart--default',
    isFsLayout ? 'pdc-chart--fs-layout' : '',
    isModern ? 'pdc-chart--modern' : '',
    expandInToolbar ? 'pdc-chart--batch-toolbar' : '',
    batchNarrow ? 'pdc-chart--batch-narrow' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass}>
      {isSummary || isBatch ? (
        <div className="pdc-chart__controls">
          {expandInToolbar ? (
            <div className="pdc-chart__batch-header-row">
              <span className="pdc-chart__batch-title">{batchHeaderTitle}</span>
              <div className="pdc-chart__batch-header-actions">
                {downloadButton}
                {expandButton}
              </div>
            </div>
          ) : null}
          <div className="pdc-chart__controls-row">
            <NmSegmentControl fullWidth ariaLabel={t('pdcChart.title')} value={peakType} onChange={onPeakTypeChange}
              options={peakOptions.map(option => ({ value: option.type, label: option.label }))} />
            {isMulti && peakType === 'pw_peak' && onValueScaleChange && (
              <div className="pdc-chart__scale-segment" role="group" aria-label={t('compare.batch.pdcScale')}>
                <button
                  type="button"
                  className={`pdc-chart__scale-btn${valueScale === 'absolute' ? ' pdc-chart__scale-btn--active' : ''}`}
                  onClick={() => onValueScaleChange('absolute')}
                >
                  W
                </button>
                <button
                  type="button"
                  className={`pdc-chart__scale-btn${valueScale === 'perKg' ? ' pdc-chart__scale-btn--active' : ''}`}
                  onClick={() => onValueScaleChange('perKg')}
                >
                  W/kg
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="pdc-chart__toolbar">
            <div className="pdc-chart__title">{meta.title}</div>
            <NmSegmentControl fullWidth ariaLabel={meta.title} value={peakType} onChange={onPeakTypeChange}
              options={peakOptions.map(option => ({ value: option.type, label: option.label }))} />
          </div>
          {!hideCompareControls && (
            <div className="pdc-chart__compare-row">
              <span className="pdc-chart__compare-label">{t('pdcChart.compare')}</span>
              {compareDates}
              {loading && <span className="pdc-chart__loading"><NmLoader size="sm" inline label={t('common.loading')} /></span>}
            </div>
          )}
        </>
      )}

      <div ref={plotWrapRef} className="pdc-chart__plot-wrap">
        {!hasData ? (
          <div
            className={`pdc-chart__empty${loading ? ' pdc-chart__empty--loading' : ''}`}
            style={useResponsivePlot ? { width: '100%', height: responsivePlotHeight } : { height: H }}
          >
            {loading ? <NmLoader size="sm" /> : t('pdcChart.empty')}
          </div>
        ) : (
        <div
          ref={wrapRef}
          className={`chart-touch-plot pdc-chart__plot ltr-island${useResponsivePlot ? ' pdc-chart__plot--responsive' : ''}`}
          style={useResponsivePlot ? { width: '100%', height: responsivePlotHeight, maxWidth: '100%' } : undefined}
        >
        {!expandInToolbar ? plotActionButtons : null}
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={useResponsivePlot ? '100%' : H}
          preserveAspectRatio={isFsLayout ? 'xMidYMid slice' : 'xMidYMid meet'}
          className="pdc-chart__svg"
          style={{ touchAction: isFsLayout ? 'none' : 'pan-y pinch-zoom', display: 'block' }}
          {...plotPointerHandlers}
        >
          {isModern && (
            <defs>
              <linearGradient id={`${gradId}-line`} x1={ML} y1="0" x2={W - MR} y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="var(--chart-series-green)" />
                <stop offset="45%" stopColor="var(--chart-series-green)" />
                <stop offset="100%" stopColor="var(--chart-series-green)" />
              </linearGradient>
              <linearGradient id={`${gradId}-fill-fade`} x1={ML} y1={MT} x2={ML} y2={MT + CH} gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.16" />
                <stop offset="55%" stopColor="#ffffff" stopOpacity="0.07" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0.04" />
              </linearGradient>
              <mask id={`${gradId}-fill-mask`}>
                {currentAreaPath ? <path d={currentAreaPath} fill={`url(#${gradId}-fill-fade)`} /> : null}
              </mask>
              <linearGradient id={`${gradId}-compare-fill`} x1="0" y1={MT} x2="0" y2={MT + CH} gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={CHART_SERIES.secondary} stopOpacity="0.22" />
                <stop offset="100%" stopColor={CHART_SERIES.secondary} stopOpacity="0.02" />
              </linearGradient>
            </defs>
          )}

          {/* Y grid */}
          {yTicks.map(v => {
            const y = valueToPlotY(v, yMin, yMax, MT, CH);
            return (
              <g key={v}>
                <line
                  x1={ML}
                  y1={y}
                  x2={W - MR}
                  y2={y}
                  className="pdc-chart__grid-line"
                  stroke={isModern ? 'rgba(154,161,177,0.38)' : GRID}
                  strokeWidth={1}
                  strokeDasharray={isModern ? '4 5' : undefined}
                />
                <text
                  x={ML - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fontSize={axisFs}
                  className="pdc-chart__axis-text pdc-chart__axis-text--y"
                  fill="currentColor"
                >
                  {displayDigits(formatPdcYTick(v, valueScale === 'perKg'))}
                </text>
              </g>
            );
          })}

          <text
            x={unitLabelX}
            y={MT + CH / 2}
            fontSize={Math.max(10, Math.round(axisFs * 0.85))}
            className="pdc-chart__axis-text pdc-chart__axis-text--unit"
            fill="currentColor"
            transform={`rotate(-90 ${unitLabelX} ${MT + CH / 2})`}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {displayUnit.toUpperCase()}
          </text>

          {isMulti ? (
            <>
              {multiSeriesPlot.seriesLines.map(line => (
                line.linePath ? (
                  <path
                    key={line.id}
                    d={line.linePath}
                    fill="none"
                    stroke={line.color}
                    strokeDasharray={line.dash}
                    strokeWidth={lineW}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity={0.92}
                  />
                ) : null
              ))}
            </>
          ) : isModern ? (
            <>
              {compareAreaPath && <path d={compareAreaPath} fill={`url(#${gradId}-compare-fill)`} />}
              {currentAreaPath && (
                <path
                  d={currentAreaPath}
                  fill={`url(#${gradId}-line)`}
                  mask={`url(#${gradId}-fill-mask)`}
                />
              )}
              {compareLinePath && (
                <path
                  d={compareLinePath}
                  className="pdc-chart__compare-line"
                  strokeDasharray={seriesStrokeDash(1)}
                  fill="none"
                  stroke={CHART_SERIES.secondary}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={0.85}
                />
              )}
              {currentLinePath && (
                <path
                  d={currentLinePath}
                  className="pdc-chart__current-line"
                  fill="none"
                  stroke={`url(#${gradId}-line)`}
                  strokeWidth={lineW}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
            </>
          ) : (
            <>
              {comparePath && <path d={comparePath} fill={COMPARE} />}
              {currentPath && (
                <>
                  <path d={currentPath} fill={CURRENT} fillOpacity={0.85} />
                  {currentCurve.length > 1 && (
                    <polyline
                      fill="none"
                      stroke={CURRENT_EDGE}
                      strokeWidth={1.2}
                      points={currentCurve.map(p => {
                        const x = logX(p.interval, minI, maxI, ML, CW);
                        const y = valueToPlotY(p.value, yMin, yMax, MT, CH);
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                  )}
                </>
              )}
            </>
          )}

          {/* X axis labels */}
          {axisLabels.map(p => {
            const x = logX(p.interval, minI, maxI, ML, CW);
            const isLongDurationTick = isSummary && p.interval >= 3600;
            const labelFontSize = isLongDurationTick ? axisFs * 0.78 : axisFs;
            const textAnchor = x <= ML + axisFs
              ? 'start'
              : x >= W - MR - axisFs
                ? 'end'
                : 'middle';
            return (
              <text
                key={p.key}
                x={x}
                y={axisLabelY}
                textAnchor={textAnchor}
                dominantBaseline="ideographic"
                fontSize={labelFontSize}
                letterSpacing={isLongDurationTick ? '-0.04em' : undefined}
                className="pdc-chart__axis-text pdc-chart__axis-text--x"
                fill="currentColor"
              >
                {displayDigits(formatPdcXAxisTick(p.interval))}
              </text>
            );
          })}

          {/* Hover crosshair + markers */}
          {hoverRow && hoverX != null && (
            <g pointerEvents="none">
              <line
                x1={hoverX}
                y1={MT}
                x2={hoverX}
                y2={MT + CH}
                stroke="var(--chart-grid, rgba(26,31,43,.2))"
                strokeWidth={1.5}
                strokeDasharray="4 4"
              />
              {isMulti && hoverRow && 'entries' in hoverRow ? (
                hoverRow.entries
                  .slice()
                  .sort((a, b) => b.value - a.value)
                  .map(entry => (
                    <circle
                      key={entry.seriesId}
                      cx={hoverX}
                      cy={valueToPlotY(entry.value, yMin, yMax, MT, CH)}
                      r={markerR}
                      fill="#fff"
                      stroke={entry.color}
                      strokeWidth={2.2}
                    />
                  ))
              ) : (
                <>
                  {hoverYCompare != null && (
                    <circle cx={hoverX} cy={hoverYCompare} r={markerR} fill="#fff" stroke={CHART_SERIES.secondary} strokeWidth={2.2} />
                  )}
                  {hoverYCurrent != null && (
                    <circle
                      cx={hoverX}
                      cy={hoverYCurrent}
                      r={markerR + 0.5}
                      fill="#fff"
                      stroke={isModern ? `url(#${gradId}-line)` : CURRENT}
                      strokeWidth={2.8}
                    />
                  )}
                </>
              )}
            </g>
          )}
        </svg>

        {hoverRow && hoverX != null && (() => {
          const wrapW = wrapRef.current?.clientWidth ?? 300;
          const isMultiRow = isMulti && 'entries' in hoverRow;
          const dualRow = !isMultiRow ? (hoverRow as PdcHoverRow) : null;
          const tooltipW = isMultiRow ? 220 : isSummary || isBatch ? 152 : 168;
          const hoverDelta = dualRow?.current != null && dualRow.compare != null
            ? intervalDeltaPct(dualRow.current, dualRow.compare)
            : null;
          const multiEntries = isMultiRow
            ? [...hoverRow.entries].sort((a, b) => b.value - a.value)
            : [];
          const maxMultiVal = multiEntries.length ? multiEntries[0].value : 0;
          return (
          <div
            className={[
              'pdc-chart__tooltip',
              isSummary || isBatch ? 'pdc-chart__tooltip--compact' : '',
              isMultiRow ? 'pdc-chart__tooltip--multi' : '',
            ].filter(Boolean).join(' ')}
            style={{
              left: Math.max(8, Math.min(tooltipLeftPx, wrapW - tooltipW - 8)),
              transform: tooltipLeftPx > wrapW * 0.55 ? 'translateX(-100%)' : 'none',
            }}
          >
            <div className="pdc-chart__tooltip-title">
              {axisLabelText(hoverRow)}
            </div>
            {isMultiRow ? (
              <div className="pdc-chart__tooltip-multi">
                {multiEntries.map(entry => {
                  const delta = maxMultiVal > 0
                    ? ((entry.value - maxMultiVal) / maxMultiVal) * 100
                    : null;
                  return (
                    <div key={entry.seriesId} className="pdc-chart__tooltip-row">
                      <span className="pdc-chart__tooltip-key">
                        <span className="pdc-chart__swatch" style={{ background: entry.color }} />
                        <span className="pdc-chart__tooltip-series-label">{entry.label}</span>
                      </span>
                      <span className="pdc-chart__tooltip-val">
                        {fmtPdcValue(entry.value, displayUnit)}
                        <span className="pdc-chart__tooltip-unit">{displayUnit}</span>
                        {delta != null && entry.value < maxMultiVal && (
                          <span className={`pdc-chart__tooltip-inline-delta${delta < 0 ? ' metric-value--negative' : ''}`}>
                            {displayDigits(formatDeltaPct(delta))}
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : isSummary || isBatch ? (
              <>
                <div className="pdc-chart__tooltip-values">
                  {dualRow?.compare != null && (
                    <span className="pdc-chart__tooltip-num pdc-chart__tooltip-num--compare">
                      {fmtPdcValue(dualRow.compare, displayUnit)}
                      <span className="pdc-chart__tooltip-unit">{displayUnit}</span>
                    </span>
                  )}
                  {dualRow?.compare != null && dualRow.current != null && (
                    <span className="pdc-chart__tooltip-sep" aria-hidden>·</span>
                  )}
                  {dualRow?.current != null && (
                    <span className="pdc-chart__tooltip-num pdc-chart__tooltip-num--current">
                      {fmtPdcValue(dualRow.current, displayUnit)}
                      <span className="pdc-chart__tooltip-unit">{displayUnit}</span>
                    </span>
                  )}
                </div>
                {hoverDelta != null && (
                  <div
                    className={`pdc-chart__tooltip-delta pdc-chart__tooltip-delta--${hoverDelta >= 0 ? 'up' : 'down'}`}
                  >
                    {displayDigits(formatDeltaPct(hoverDelta))}
                  </div>
                )}
              </>
            ) : (
              <>
                {dualRow?.compare != null && (
                  <div className="pdc-chart__tooltip-row">
                    <span className="pdc-chart__tooltip-key">
                      <span className="pdc-chart__swatch pdc-chart__swatch--compare" />
                      {t('pdcChart.best')}
                    </span>
                    <span className="pdc-chart__tooltip-val">
                      {fmtPdcValue(dualRow.compare, displayUnit)} <span className="pdc-chart__tooltip-unit">{displayUnit}</span>
                    </span>
                  </div>
                )}
                {dualRow?.current != null && (
                  <div className="pdc-chart__tooltip-row">
                    <span className="pdc-chart__tooltip-key">
                      <span className="pdc-chart__swatch pdc-chart__swatch--current" />
                      {currentSeriesLabel}
                    </span>
                    <span className="pdc-chart__tooltip-val pdc-chart__tooltip-val--current">
                      {fmtPdcValue(dualRow.current, displayUnit)} <span className="pdc-chart__tooltip-unit">{displayUnit}</span>
                    </span>
                  </div>
                )}
                {hoverDelta != null && (
                  <div
                    className={`pdc-chart__tooltip-delta pdc-chart__tooltip-delta--${hoverDelta >= 0 ? 'up' : 'down'}`}
                  >
                    {displayDigits(formatDeltaPct(hoverDelta))}
                  </div>
                )}
              </>
            )}
          </div>
          );
        })()}
        {loading && (
          <div
            className="pdc-chart__loading-overlay"
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={t('common.loading')}
          >
            <NmLoader size="sm" />
          </div>
        )}
        </div>
        )}
      </div>

      {hasData && isMulti && multiSeries && (
        <div className="pdc-chart__legend pdc-chart__legend--batch">
          {multiSeries.map((s, index) => {
            const hidden = hiddenSeriesIds?.has(s.id) ?? false;
            return (
              <button
                key={s.id}
                type="button"
                className={`pdc-chart__legend-item pdc-chart__legend-item--toggle${hidden ? ' pdc-chart__legend-item--hidden' : ''}`}
                onClick={() => onToggleSeries?.(s.id)}
                aria-pressed={!hidden}
              >
                <ChartSeriesSwatch color={s.color} dash={seriesStrokeDash(index)} />
                <span className="pdc-chart__legend-value">{s.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {hasData && isSummary && !isMulti && !hideLegend && (
        <div className="pdc-chart__legend pdc-chart__legend--minimal">
          {currentCurve.length > 0 && (
            <span className="pdc-chart__legend-item pdc-chart__legend-item--today">
              <span className="pdc-chart__swatch pdc-chart__swatch--current" aria-hidden />
              <span className="pdc-chart__legend-value">{currentSeriesLabel}</span>
            </span>
          )}
          {compareDeltaPct != null && (
            <span
              className={`pdc-chart__legend-delta pdc-chart__legend-delta--${compareDeltaPct >= 0 ? 'up' : 'down'}`}
            >
              {displayDigits(formatDeltaPct(compareDeltaPct))}
            </span>
          )}
          {compareCurve.length > 0 && (
            <span className="pdc-chart__legend-item pdc-chart__legend-item--compare">
              <span className="pdc-chart__swatch pdc-chart__swatch--compare" aria-hidden />
              <span className="pdc-chart__legend-value">{compareLegend}</span>
            </span>
          )}
        </div>
      )}

      {hasData && !isSummary && !isBatch && !hideLegend && (
        <div className="pdc-chart__legend">
          {currentCurve.length > 0 && (
            <span className="pdc-chart__legend-item">
              <span className="pdc-chart__swatch pdc-chart__swatch--current" aria-hidden />
              {currentSeriesLabel}
            </span>
          )}
          {compareCurve.length > 0 && (
            <span className="pdc-chart__legend-item">
              <span className="pdc-chart__swatch pdc-chart__swatch--compare" aria-hidden />
              {compareLegend}
            </span>
          )}
        </div>
      )}

      {bestWorkoutSelection && typeof document !== 'undefined' && createPortal(
        <div
          className="pdc-best-workout-sheet__backdrop"
          onClick={() => setBestWorkoutSelection(null)}
        >
          <section
            className="pdc-best-workout-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdc-best-workout-sheet-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="pdc-best-workout-sheet__handle" aria-hidden />
            <header className="pdc-best-workout-sheet__header">
              <div>
                <h3 id="pdc-best-workout-sheet-title">
                  {t('pdcChart.bestWorkoutSheet.title')}
                </h3>
                <p>
                  {t('pdcChart.bestWorkoutSheet.subtitle', {
                    interval: axisLabelText(bestWorkoutSelection.row),
                  })}
                </p>
              </div>
              <button
                type="button"
                className="pdc-best-workout-sheet__close"
                onClick={() => setBestWorkoutSelection(null)}
                aria-label={t('pdcChart.close')}
              >
                ×
              </button>
            </header>

            <button
              type="button"
              className="pdc-best-workout-sheet__workout"
              onClick={() => {
                const workoutId = bestWorkoutSelection.workoutId;
                setBestWorkoutSelection(null);
                onBestWorkoutClick?.(workoutId);
              }}
            >
              <span className="pdc-best-workout-sheet__icon" aria-hidden>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 4h8v3a4 4 0 0 1-8 0V4Z" />
                  <path d="M12 11v5M9 20h6M12 16a4 4 0 0 0 4 4M12 16a4 4 0 0 1-4 4M8 6H5v1a4 4 0 0 0 4 4M16 6h3v1a4 4 0 0 1-4 4" />
                </svg>
              </span>
              <span className="pdc-best-workout-sheet__copy">
                <strong>{t('pdcChart.bestWorkoutSheet.cardTitle')}</strong>
                <span>
                  {t('pdcChart.bestWorkoutSheet.cardMeta', {
                    interval: axisLabelText(bestWorkoutSelection.row),
                    value: fmtPdcValue(
                      bestWorkoutSelection.workoutId === bestWorkoutSelection.row.compareWorkoutId
                        ? bestWorkoutSelection.row.compare ?? bestWorkoutSelection.row.current ?? 0
                        : bestWorkoutSelection.row.current ?? bestWorkoutSelection.row.compare ?? 0,
                      displayUnit,
                    ),
                    unit: displayUnit,
                  })}
                </span>
              </span>
              <span className="pdc-best-workout-sheet__chevron" aria-hidden>›</span>
            </button>
          </section>
        </div>,
        document.body,
      )}
    </div>
  );
}

export { PEAK_META as PEAK_OPTIONS };
